import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cjmsApi } from '../api/cjmsApi';

interface GoalToGapAssessmentProps {
  leadId: string;
  email: string;
  onComplete?: (assessment: any) => void;
  onCancel?: () => void;
}

interface AssessmentFormData {
  // Current Situation
  currentRevenue: string;
  customerCount: string;
  averageTransactionValue: string;
  // Goals
  sixMonthTarget: string;
  freedomNumber: string;
  additionalCustomersNeeded: string;
  idealCustomerLifetimeValue: string;
  // Website Needs
  websiteProblem: string;
  visitorOutcome: string;
  keyMessage: string;
  callToAction: string;
  // Inspiration
  inspirationWebsites: string;
  brandPersonality: string;
  colorPreferences: string;
}

const STEPS = [
  { id: 1, title: 'Current Situation', subtitle: 'Where is your business today?' },
  { id: 2, title: 'Your Goals', subtitle: 'Where do you want to be?' },
  { id: 3, title: 'Website Needs', subtitle: 'What should your website achieve?' },
  { id: 4, title: 'Inspiration', subtitle: 'What inspires you?' },
];

const BRAND_PERSONALITIES = [
  'Professional', 'Friendly', 'Bold', 'Elegant', 'Playful', 'Minimalist', 'Luxurious', 'Approachable'
];

const CTA_OPTIONS = [
  'Call us', 'Buy now', 'Contact form', 'Book appointment', 'Request quote', 'Download', 'Sign up'
];

export function GoalToGapAssessment({ leadId, email, onComplete, onCancel }: GoalToGapAssessmentProps) {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<AssessmentFormData>({
    currentRevenue: '',
    customerCount: '',
    averageTransactionValue: '',
    sixMonthTarget: '',
    freedomNumber: '',
    additionalCustomersNeeded: '',
    idealCustomerLifetimeValue: '',
    websiteProblem: '',
    visitorOutcome: '',
    keyMessage: '',
    callToAction: '',
    inspirationWebsites: '',
    brandPersonality: '',
    colorPreferences: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState<any>(null);

  const updateField = (field: keyof AssessmentFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculatePreview = () => {
    const currentRev = parseFloat(formData.currentRevenue) || 0;
    const freedomNum = parseFloat(formData.freedomNumber) || 0;
    const gap = freedomNum - currentRev;
    const avgTx = parseFloat(formData.averageTransactionValue) || 0;
    const additionalCustomers = avgTx > 0 ? Math.ceil(gap / avgTx) : 0;
    return { currentRev, freedomNum, gap, additionalCustomers };
  };

  const handleNext = () => {
    if (step < STEPS.length) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await cjmsApi.submitAssessment({
        leadId,
        email,
        ...formData,
      });

      if (result.success) {
        setSuccess(true);
        setAssessmentResult(result.assessment);
        if (onComplete) {
          onComplete(result.assessment);
        }
      } else {
        setError(result.error || 'Failed to submit assessment');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const preview = calculatePreview();

  if (success && assessmentResult) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg border border-slate-200 p-8"
        >
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">✓</span>
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Assessment Complete!</h2>
            <p className="text-slate-600">Here's your personalized analysis</p>
          </div>

          <div className="space-y-6">
            {/* Current State */}
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">Current State Analysis</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Monthly Revenue:</span>
                  <p className="font-medium">R{assessmentResult.currentState.revenue.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-slate-500">Customers:</span>
                  <p className="font-medium">{assessmentResult.currentState.customers}</p>
                </div>
                <div>
                  <span className="text-slate-500">Avg Transaction:</span>
                  <p className="font-medium">R{assessmentResult.currentState.avgTransaction.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-slate-500">Maturity Level:</span>
                  <p className="font-medium">{assessmentResult.currentState.maturityLevel}</p>
                </div>
              </div>
            </div>

            {/* Freedom Number */}
            <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <h3 className="font-semibold text-indigo-800 mb-2">Your Freedom Number</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-indigo-600">Target Monthly Revenue:</span>
                  <p className="font-bold text-lg text-indigo-800">R{assessmentResult.freedomNumber.target.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-indigo-600">Revenue Gap:</span>
                  <p className="font-bold text-lg text-indigo-800">R{assessmentResult.freedomNumber.gap.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-indigo-600">Additional Customers Needed:</span>
                  <p className="font-bold text-lg text-indigo-800">{assessmentResult.freedomNumber.additionalCustomersNeeded}</p>
                </div>
              </div>
            </div>

            {/* Recommendations */}
            {assessmentResult.recommendations && assessmentResult.recommendations.length > 0 && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="font-semibold text-amber-800 mb-2">Recommendations</h3>
                <ul className="space-y-2">
                  {assessmentResult.recommendations.map((rec: string, i: number) => (
                    <li key={i} className="text-sm text-amber-900 flex items-start gap-2">
                      <span className="text-amber-600 mt-0.5">•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={onCancel}
              className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 transition-colors"
            >
              Continue to Next Step
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
          <h2 className="text-2xl font-bold mb-1">Goal-to-Gap Assessment</h2>
          <p className="text-indigo-100 text-sm">Let's discover your Freedom Number and create a roadmap</p>
          
          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((s) => (
              <div key={s.id} className="flex items-center gap-1">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  step >= s.id ? 'bg-white text-indigo-600' : 'bg-indigo-500 text-indigo-200'
                }`}>
                  {step > s.id ? '✓' : s.id}
                </div>
                {s.id < STEPS.length && (
                  <div className={`w-8 h-0.5 ${step > s.id ? 'bg-white' : 'bg-indigo-400'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form body */}
        <div className="p-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Current Situation */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-slate-800">Current Situation</h3>
                  <p className="text-sm text-slate-500">Tell us about your business today</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What's your current monthly revenue from your business?
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-500">R</span>
                    <input
                      type="number"
                      value={formData.currentRevenue}
                      onChange={(e) => updateField('currentRevenue', e.target.value)}
                      className="w-full pl-8 pr-3 p-2 border border-slate-300 rounded-md text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    How many customers do you currently have?
                  </label>
                  <input
                    type="number"
                    value={formData.customerCount}
                    onChange={(e) => updateField('customerCount', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    placeholder="0"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What's your average transaction value?
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-500">R</span>
                    <input
                      type="number"
                      value={formData.averageTransactionValue}
                      onChange={(e) => updateField('averageTransactionValue', e.target.value)}
                      className="w-full pl-8 pr-3 p-2 border border-slate-300 rounded-md text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Goals */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-slate-800">Your Goals</h3>
                  <p className="text-sm text-slate-500">Where do you want your business to be?</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Where do you want your business to be in 6 months?
                  </label>
                  <textarea
                    value={formData.sixMonthTarget}
                    onChange={(e) => updateField('sixMonthTarget', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    rows={2}
                    placeholder="Describe your 6-month vision..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What's your target monthly revenue (Freedom Number)?
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-500">R</span>
                    <input
                      type="number"
                      value={formData.freedomNumber}
                      onChange={(e) => updateField('freedomNumber', e.target.value)}
                      className="w-full pl-8 pr-3 p-2 border border-slate-300 rounded-md text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                  {parseFloat(formData.freedomNumber) > 0 && parseFloat(formData.currentRevenue) > 0 && (
                    <div className="mt-2 p-3 bg-indigo-50 rounded-md text-sm">
                      <span className="text-indigo-700 font-medium">Freedom Number Gap: </span>
                      <span className="text-indigo-900 font-bold">
                        R{(parseFloat(formData.freedomNumber) - parseFloat(formData.currentRevenue)).toLocaleString()}/mo
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    How many additional customers do you want?
                  </label>
                  <input
                    type="number"
                    value={formData.additionalCustomersNeeded}
                    onChange={(e) => updateField('additionalCustomersNeeded', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    placeholder="0"
                    min="0"
                  />
                  {preview.additionalCustomers > 0 && (
                    <div className="mt-2 p-3 bg-green-50 rounded-md text-sm">
                      <span className="text-green-700 font-medium">Based on your average transaction: </span>
                      <span className="text-green-900 font-bold">
                        You need ~{preview.additionalCustomers} more customers to close the gap
                      </span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What's your ideal customer lifetime value?
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-500">R</span>
                    <input
                      type="number"
                      value={formData.idealCustomerLifetimeValue}
                      onChange={(e) => updateField('idealCustomerLifetimeValue', e.target.value)}
                      className="w-full pl-8 pr-3 p-2 border border-slate-300 rounded-md text-sm"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Website Needs */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-slate-800">Website Needs</h3>
                  <p className="text-sm text-slate-500">What should your website achieve?</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What problem does your website solve for customers?
                  </label>
                  <textarea
                    value={formData.websiteProblem}
                    onChange={(e) => updateField('websiteProblem', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    rows={2}
                    placeholder="e.g., Makes it easy to book appointments, showcases our work..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What results do you want visitors to achieve?
                  </label>
                  <textarea
                    value={formData.visitorOutcome}
                    onChange={(e) => updateField('visitorOutcome', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    rows={2}
                    placeholder="e.g., Book a free consultation, Request a quote..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What's the most important message you want to convey?
                  </label>
                  <textarea
                    value={formData.keyMessage}
                    onChange={(e) => updateField('keyMessage', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    rows={2}
                    placeholder="e.g., Trusted by 500+ homeowners, Quality guaranteed..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What should a visitor do after visiting? (Call-to-Action)
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {CTA_OPTIONS.map((cta) => (
                      <button
                        key={cta}
                        type="button"
                        onClick={() => updateField('callToAction', cta)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          formData.callToAction === cta
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-300'
                        }`}
                      >
                        {cta}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Inspiration */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-4"
              >
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-slate-800">Inspiration</h3>
                  <p className="text-sm text-slate-500">What inspires you?</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What websites inspire you? (Share 2-3 examples)
                  </label>
                  <textarea
                    value={formData.inspirationWebsites}
                    onChange={(e) => updateField('inspirationWebsites', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    rows={2}
                    placeholder="e.g., apple.com, nike.com, local-coffee-shop.co.za..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    What's your brand personality?
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {BRAND_PERSONALITIES.map((personality) => (
                      <button
                        key={personality}
                        type="button"
                        onClick={() => updateField('brandPersonality', personality)}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          formData.brandPersonality === personality
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-300'
                        }`}
                      >
                        {personality}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Any specific colors or design elements you prefer?
                  </label>
                  <textarea
                    value={formData.colorPreferences}
                    onChange={(e) => updateField('colorPreferences', e.target.value)}
                    className="w-full p-2 border border-slate-300 rounded-md text-sm"
                    rows={2}
                    placeholder="e.g., Blue and white, modern clean look, images of nature..."
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between mt-6 pt-4 border-t border-slate-200">
            <div>
              {step > 1 && (
                <button
                  onClick={handleBack}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  ← Back
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                >
                  Cancel
                </button>
              )}
              {step < STEPS.length ? (
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-md hover:bg-indigo-700 transition-colors"
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`px-6 py-2 text-sm font-semibold rounded-md transition-colors ${
                    loading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {loading ? 'Submitting...' : 'Submit Assessment'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
